import { SyncModel, SyncDataModel, DataModel } from "./syncModel";

/** the 10 color roles of the currently active theme branch (dark or light), portal-managed */
export interface WebshopColorTokenRoles {
  brand: string;
  brandContrast: string;
  page: string;
  pageText: string;
  surface: string;
  surfaceText: string;
  surfaceAlt: string;
  surfaceAltText: string;
  muted: string;
  line: string;
}

/**
 * Explicit camelCase contract-key -> kebab-case CSS custom property name mapping for the 10 roles
 * (inverse of the server's `GetRoleContractKey`). Deliberately a fixed lookup table, not a generic
 * camelCase-to-kebab-case regex conversion - a regex would silently "convert" any future/unexpected
 * key instead of failing loudly, and would also be the wrong tool for `WebshopColorTokensService`'s
 * role-name allowlist (both the role-key iteration and the `bindings[].role` check use this
 * same table as their single source of truth).
 */
export const WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES: { [roleKey in keyof WebshopColorTokenRoles]: string } = {
  brand: "brand",
  brandContrast: "brand-contrast",
  page: "page",
  pageText: "page-text",
  surface: "surface",
  surfaceText: "surface-text",
  surfaceAlt: "surface-alt",
  surfaceAltText: "surface-alt-text",
  muted: "muted",
  line: "line",
};

/**
 * Exact allowlist of the 70 canonical ZUORDNUNG token names, mirrored 1:1 from the 70 `AddDefault("--...", ...)`
 * calls in `App_Code\ColorTokenDefaults.vb` (`GetTokenOrder()`, the authoritative Portal-side list) - not a
 * grammar/regex check (`^--[a-z0-9-]+$`), because that would still accept a syntactically-valid but unknown
 * token name. `WebshopColorTokensService` validates every `bindings[].token` against this exact set
 * before interpolating it as a CSS property name - the same reasoning and precedent as
 * `WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES` above. Must be kept in sync if `ColorTokenDefaults.vb`'s `AddDefault`
 * list ever changes - three places carry this same 70-name set today: `ColorTokenDefaults.vb` (source),
 * this list, and `AccessibilityWebShopService.COLOR_VARS` (`accessibilityWebShop.service.ts`), which needs
 * every name to compute its `--a11y-color-*` contrast replacement.
 */
export const WEBSHOP_COLOR_TOKEN_NAMES: ReadonlyArray<string> = [
  "--color-font-primary",
  "--color-font-primary-sub",
  "--color-font-secondary",
  "--color-font-secondary-sub",
  "--color-bg-primary",
  "--color-bg-secondary",
  "--color-accent",
  "--color-font-accent",
  "--color-accent-2",
  "--color-font-accent-2",
  "--color-state-error",
  "--color-bg-header",
  "--color-bg-home-label",
  "--color-font-home-label",
  "--color-bg-pageheader",
  "--color-font-pageheader",
  "--color-font-pagesubheader",
  "--color-font-favoritebar",
  "--color-font-content-header",
  "--color-font-content-subheader",
  "--color-font-content-body",
  "--color-bg-btn-modal",
  "--color-font-btn-modal",
  "--color-bg-btn-page",
  "--color-font-btn-page",
  "--color-bg-app",
  "--color-font-footer",
  "--color-accent-tabs-underline",
  "--color-bg-btn-wide",
  "--color-btn-wide",
  "--color-state-false",
  "--color-icon-co2",
  "--color-icon-like",
  "--color-accent-highlight",
  "--color-bg-btn-modal-disabled",
  "--color-font-btn-modal-disabled",
  "--color-bg-btn-wide-disabled",
  "--color-btn-wide-disabled",
  "--color-bg-btn-page-disabled",
  "--color-font-btn-page-disabled",
  "--color-bg-ripple",
  "--color-font-tab",
  "--color-bg-user-profile",
  "--color-accent-progress",
  "--color-bg-progress",
  "--color-bg-footer",
  "--color-font-header",
  "--color-font-menu",
  "--color-icon-like-disabled",
  "--color-font-content-body-disabled",
  "--color-bg-calendar-selected",
  "--color-state-option-selected",
  "--color-bg-statusbar",
  "--color-bg-user-circle",
  "--color-bg-checkbox",
  "--color-border-text",
  "--color-current",
  "--color-rating",
  "--color-border-item",
  "--color-icon-header",
  "--color-bg-sidenav",
  "--color-font-sidenav",
  "--color-bg-nav-back",
  "--color-btn-nav-back",
  "--color-border-nav-back",
  "--color-user-profile-info",
  "--color-item-description",
  "--color-detail-page-header",
  "--color-accent-sidenav",
  "--color-bg-mixed",
];

/** "role" references one of the 10 roles above via var(--role); "literal" carries a customer-set CSS value directly */
export type WebshopColorTokenBindingMode = "role" | "literal";

/**
 * One ZUORDNUNG-token binding. The server sends ALL 70 of them for the active theme, not just
 * deviations: the build-time SCSS floor is per-customer (each customer's `_custom-color-variable.scss`
 * was generated individually from that customer's original palette), so there is no shared default
 * the device could fall back to. Measured across every customer file, each one differs from the
 * reference the Portal's default table was transcribed from - see `ColorTokenDefaults.WriteBindings`.
 */
export interface WebshopColorTokenBinding {
  token: string;
  mode: WebshopColorTokenBindingMode;
  role: string;
  value: string;
}

// Requests
export class WebshopColorTokensSyncModel extends SyncModel {
}

// Responses

/** app installation belongs to exactly one customer, so this record is a device-wide singleton (no location key, unlike SOTStyle) */
export const WebshopColorTokensDMKey: number = 0;

export class WebshopColorTokensDataModel extends DataModel {
  public get DM_Key(): number | string { return WebshopColorTokensDMKey; }
  public active_theme: "dark" | "light";
  /** only the 10 roles of the active_theme branch travel over the wire */
  public roles: WebshopColorTokenRoles;
  /** all 70 ZUORDNUNG tokens of the active theme, in GetTokenOrder order - never a partial set, see WebshopColorTokenBinding */
  public bindings: WebshopColorTokenBinding[];
}

export class WebshopColorTokensSyncDataModel extends SyncDataModel {
  public color_tokens: WebshopColorTokensDataModel[];
  public get models(): DataModel[] { return this.color_tokens; }
  public DeserializeType(): void {
    this._DeserializeType(WebshopColorTokensDataModel);
  }
}
